import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, Card, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ar, enUS } from "date-fns/locale";
import HelmetMeta from "../../components/HelmetMeta";
import { AppContext } from "../../AppContext";
import { useLocalization } from "../../i18n/LocalizationProvider";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import { useGetDocTypeList } from "../../shared/hooks/useFrappeDoctype";
import { useSetStatusCommand } from "../../shared/hooks/useProductionCenter";
import { useSetOrderReadyTime } from "../../shared/hooks/useTableOrdersData";
import {
    KitchenDashboardHeader,
    KitchenOrderCard,
    KitchenOrderDetailsCard,
} from "./components";
import { useKitchenItemAdvanceQueue, useKitchenLateAlerts } from "./hooks";
import {
    getOrderCreatedAt,
    getOrderLastFoodReadyAt,
    isCompletedStatus,
    isLikelyDocName,
    isOrderLate,
} from "./utils";

function KitchenDashboard() {
    const { t } = useTranslation();
    const { language } = useLocalization();
    const { socket, foodLateTimeEnabled, foodLateTime, restaurantSettings } =
        useContext(AppContext);
    const { productionCenter } = useParams();
    const distanceLocale = language === "ar" ? ar : enUS;
    const routeSelectionKey = productionCenter || "__all__";
    const initializedSelectionRef = useRef(false);

    const showItemsOnKds =
        Number(restaurantSettings?.docs?.[0]?.show_items_on_kds) === 1;
    const [activeOrderReadyName, setActiveOrderReadyName] = useState(null);
    const [detailOrderName, setDetailOrderName] = useState(null);
    const [selectedCenterNames, setSelectedCenterNames] = useState([]);

    const {
        data: productionCentersData,
        isLoading: isProductionCentersLoading,
    } =
        useGetDocTypeListWithChildren({
            doctype: "Restaurant Object",
            filters: [["type", "=", "Production Center"]],
        });

    const productionCenters = useMemo(
        () => (Array.isArray(productionCentersData) ? productionCentersData : []),
        [productionCentersData]
    );

    const resolvedRouteCenterNames = useMemo(() => {
        if (!productionCenter) return [];

        const field = isLikelyDocName(productionCenter) ? "name" : "description";
        const match = productionCenters.find(
            (center) =>
                String(center?.[field] || "").trim() ===
                String(productionCenter || "").trim()
        );

        return match?.name ? [match.name] : [];
    }, [productionCenter, productionCenters]);

    useEffect(() => {
        initializedSelectionRef.current = false;
        setSelectedCenterNames([]);
        setDetailOrderName(null);
    }, [routeSelectionKey]);

    useEffect(() => {
        if (initializedSelectionRef.current || isProductionCentersLoading) {
            return;
        }

        if (productionCenters.length === 0) {
            initializedSelectionRef.current = true;
            return;
        }

        if (productionCenter) {
            setSelectedCenterNames(resolvedRouteCenterNames);
            initializedSelectionRef.current = true;
            return;
        }

        setSelectedCenterNames(
            productionCenters.map((center) => center?.name).filter(Boolean)
        );
        initializedSelectionRef.current = true;
    }, [
        isProductionCentersLoading,
        productionCenter,
        productionCenters,
        resolvedRouteCenterNames,
    ]);

    useEffect(() => {
        if (selectedCenterNames.length === 0 || productionCenters.length === 0) return;

        const validNames = new Set(
            productionCenters.map((center) => center?.name).filter(Boolean)
        );
        const nextSelectedNames = selectedCenterNames.filter((name) =>
            validNames.has(name)
        );

        if (nextSelectedNames.length !== selectedCenterNames.length) {
            setSelectedCenterNames(nextSelectedNames);
        }
    }, [productionCenters, selectedCenterNames]);

    const selectedCenters = useMemo(() => {
        if (!selectedCenterNames.length) return [];

        return selectedCenterNames
            .map((name) =>
                productionCenters.find((center) => center?.name === name)
            )
            .filter(Boolean);
    }, [productionCenters, selectedCenterNames]);

    const itemGroupToCenterNames = useMemo(() => {
        const map = new Map();

        selectedCenters.forEach((center) => {
            (center?.production_center_group || []).forEach((group) => {
                const itemGroup = String(group?.item_group || "").trim();
                if (!itemGroup) return;

                if (!map.has(itemGroup)) {
                    map.set(itemGroup, []);
                }

                map.get(itemGroup).push(center.name);
            });
        });

        return map;
    }, [selectedCenters]);

    const allowedItemGroups = useMemo(() => {
        const set = new Set();

        itemGroupToCenterNames.forEach((centerNames, itemGroup) => {
            if (centerNames.length > 0 && itemGroup) {
                set.add(itemGroup);
            }
        });

        return set;
    }, [itemGroupToCenterNames]);

    const filterItemsForCenters = useCallback(
        (items) => {
            const safe = Array.isArray(items) ? items : [];
            if (selectedCenters.length === 0 || allowedItemGroups.size === 0) return [];

            return safe.filter((item) => {
                const itemGroup = String(item?.item_group || "").trim();
                return itemGroup && allowedItemGroups.has(itemGroup);
            });
        },
        [allowedItemGroups, selectedCenters.length]
    );

    const filterItemClustersForCenters = useCallback(
        (items) => {
            const safe = Array.isArray(items) ? items : [];
            if (selectedCenters.length === 0 || allowedItemGroups.size === 0) return [];

            const byId = new Map();
            safe.forEach((item) => {
                if (item?.identifier) byId.set(item.identifier, item);
            });

            const getRootId = (item) => {
                let current = item;
                let safety = 0;

                while (
                    safety < 10 &&
                    current &&
                    current.parent_item &&
                    current.parent_item !== "NULL" &&
                    byId.has(current.parent_item)
                ) {
                    current = byId.get(current.parent_item);
                    safety += 1;
                }

                return current?.identifier || item?.identifier || item?.name || null;
            };

            const clusters = new Map();
            safe.forEach((item) => {
                const rootId = getRootId(item);
                if (!rootId) return;

                if (!clusters.has(rootId)) {
                    clusters.set(rootId, []);
                }

                clusters.get(rootId).push(item);
            });

            const visibleItems = [];
            clusters.forEach((clusterItems) => {
                const isAllowed = clusterItems.some((item) => {
                    const itemGroup = String(item?.item_group || "").trim();
                    return itemGroup && allowedItemGroups.has(itemGroup);
                });

                if (isAllowed) {
                    visibleItems.push(...clusterItems);
                }
            });

            return visibleItems;
        },
        [allowedItemGroups, selectedCenters.length]
    );

    const getMatchingCenterNamesForItem = useCallback(
        (item, items = []) => {
            const directItemGroup = String(item?.item_group || "").trim();
            const directMatches = directItemGroup
                ? itemGroupToCenterNames.get(directItemGroup) || []
                : [];

            if (directMatches.length > 0) {
                return Array.from(new Set(directMatches));
            }

            const safeItems = Array.isArray(items) ? items : [];
            if (!item?.identifier || safeItems.length === 0) {
                return [];
            }

            const byId = new Map();
            safeItems.forEach((entry) => {
                if (entry?.identifier) byId.set(entry.identifier, entry);
            });

            const getRootId = (entry) => {
                let current = entry;
                let safety = 0;

                while (
                    safety < 10 &&
                    current &&
                    current.parent_item &&
                    current.parent_item !== "NULL" &&
                    byId.has(current.parent_item)
                ) {
                    current = byId.get(current.parent_item);
                    safety += 1;
                }

                return current?.identifier || entry?.identifier || null;
            };

            const targetRootId = getRootId(item);
            if (!targetRootId) return [];

            const clusterMatches = new Set();
            safeItems.forEach((entry) => {
                if (getRootId(entry) !== targetRootId) return;

                const itemGroup = String(entry?.item_group || "").trim();
                const centerNames = itemGroup
                    ? itemGroupToCenterNames.get(itemGroup) || []
                    : [];

                centerNames.forEach((centerName) => clusterMatches.add(centerName));
            });

            return Array.from(clusterMatches);
        },
        [itemGroupToCenterNames]
    );

    const resolveProductionCenterNameForItem = useCallback(
        (item, items = []) => {
            const centerNames = getMatchingCenterNamesForItem(item, items);
            return centerNames.length === 1 ? centerNames[0] : null;
        },
        [getMatchingCenterNamesForItem]
    );

    const transitionMapsByCenter = useMemo(() => {
        const map = new Map();

        selectedCenters.forEach((center) => {
            const transitionMap = new Map();

            (center?.status_managed || []).forEach((row) => {
                if (
                    row?.status_managed &&
                    row?.next_status &&
                    !transitionMap.has(row.status_managed)
                ) {
                    transitionMap.set(row.status_managed, row.next_status);
                }
            });

            map.set(center.name, transitionMap);
        });

        return map;
    }, [selectedCenters]);

    const getNextStatus = useCallback(
        (item, items = []) => {
            const productionCenterName = resolveProductionCenterNameForItem(
                item,
                items
            );
            if (!productionCenterName) return null;

            return (
                transitionMapsByCenter
                    .get(productionCenterName)
                    ?.get(item?.status) || null
            );
        },
        [resolveProductionCenterNameForItem, transitionMapsByCenter]
    );

    const { data: users } = useGetDocTypeList({
        doctype: "User",
        fields: ["name", "full_name"],
    });

    const {
        data: orders,
        isLoading,
        refetch,
        isFetching,
    } = useGetDocTypeListWithChildren({
        doctype: "Table Order",
        filters: [["status", "=", "Attending"]],
        order_by: "creation asc",
    });

    const normalizedOrders = useMemo(
        () => (Array.isArray(orders) ? orders : []),
        [orders]
    );
    const orderNames = useMemo(
        () => normalizedOrders.map((o) => o?.name).filter(Boolean),
        [normalizedOrders]
    );

    const { data: orderHistory } = useGetDocTypeList({
        doctype: orderNames.length > 0 ? "Order History" : null,
        filters: orderNames.length > 0 ? [["name", "in", orderNames]] : null,
    });

    const usersByName = useMemo(() => {
        const list = Array.isArray(users) ? users : [];
        const m = new Map();
        list.forEach((u) => {
            if (!u?.name) return;
            const label = (u?.full_name || "").trim();
            m.set(u.name, label);
        });
        return m;
    }, [users]);

    const markReadyMutation = useSetOrderReadyTime();
    const { mutate: setStatusCommand } = useSetStatusCommand();
    const thresholdMinutes = Number(foodLateTime);

    useEffect(() => {
        if (!socket) return;
        socket.emit("doctype_subscribe", "Table Order");
        const handler = (data) => {
            if (data?.doctype === "Table Order") refetch?.();
        };
        socket.on("list_update", handler);
        return () => {
            if (socket?.off) socket.off("list_update", handler);
            else if (socket?.removeListener) socket.removeListener("list_update", handler);
        };
    }, [socket, refetch]);

    useEffect(() => {
        const id = setInterval(() => {
            refetch?.();
        }, 10 * 1000);
        return () => clearInterval(id);
    }, [refetch]);

    const lastFoodReadyAtByOrderName = useMemo(() => {
        const list = Array.isArray(orderHistory) ? orderHistory : [];
        const m = new Map();
        list.forEach((h) => {
            if (!h?.name) return;
            m.set(h.name, getOrderLastFoodReadyAt(h));
        });
        return m;
    }, [orderHistory]);

    const pendingOrders = useMemo(() => {
        const base = normalizedOrders
            .filter((o) => o && !o.food_ready_time)
            .sort((a, b) => {
                const da = getOrderCreatedAt(a)?.getTime?.() ?? 0;
                const db = getOrderCreatedAt(b)?.getTime?.() ?? 0;
                return da - db;
            });

        const scoped = base.map((order) => ({
            ...order,
            __filteredEntryItems: filterItemsForCenters(order?.entry_items),
            __kdsVisibleItems: showItemsOnKds
                ? filterItemClustersForCenters(order?.entry_items)
                : filterItemsForCenters(order?.entry_items),
        }));

        return scoped.filter((order) => {
            const visibleItems = showItemsOnKds
                ? order?.__kdsVisibleItems || []
                : order?.__filteredEntryItems || [];

            if (!Array.isArray(visibleItems) || visibleItems.length === 0) return false;
            return visibleItems.some((item) => !isCompletedStatus(item?.status));
        });
    }, [
        filterItemClustersForCenters,
        filterItemsForCenters,
        normalizedOrders,
        showItemsOnKds,
    ]);

    const { flashByOrderName, nowTs } = useKitchenLateAlerts({
        normalizedOrders,
        pendingOrders,
        lastFoodReadyAtByOrderName,
        thresholdMinutes,
        foodLateTimeEnabled,
        isOrderLate,
        getOrderCreatedAt,
    });

    const selectedOrder = useMemo(() => {
        if (!detailOrderName) return null;
        return pendingOrders.find((o) => o?.name === detailOrderName) || null;
    }, [pendingOrders, detailOrderName]);

    useEffect(() => {
        if (!detailOrderName) return;
        const exists = pendingOrders.some((o) => o?.name === detailOrderName);
        if (!exists) setDetailOrderName(null);
    }, [pendingOrders, detailOrderName]);

    const markOrderReady = useCallback(
        (order, callbacks = {}) => {
            if (!order?.name || markReadyMutation.isLoading) return;
            const { onSuccess, onError } = callbacks;
            setActiveOrderReadyName(order.name);
            markReadyMutation.mutate(
                { order },
                {
                    onSuccess: () => {
                        refetch?.();
                        setDetailOrderName((prev) => (prev === order.name ? null : prev));
                        onSuccess?.();
                    },
                    onError,
                    onSettled: () => {
                        setActiveOrderReadyName((prev) =>
                            prev === order.name ? null : prev
                        );
                    },
                }
            );
        },
        [markReadyMutation, refetch]
    );

    const getDisplayItems = useCallback(
        (order) => {
            if (!order) return [];
            if (showItemsOnKds) {
                return order?.__kdsVisibleItems || [];
            }
            return order?.__filteredEntryItems || [];
        },
        [showItemsOnKds]
    );

    const {
        pendingItemIdsByOrder,
        handleAdvanceItem,
        isItemActionDisabled,
    } = useKitchenItemAdvanceQueue({
        resolveProductionCenterNameForItem,
        setStatusCommand,
        refetch,
        getNextStatus,
    });

    const selectedCenterLabel = useMemo(() => {
        if (selectedCenters.length === 1) {
            const centerLabel =
                selectedCenters[0]?.description || selectedCenters[0]?.name || "";
            return centerLabel
                ? `${centerLabel} ${t("Dashboard")}`
                : t("kds.helmetTitle");
        }

        return t("kds.helmetTitle");
    }, [selectedCenters, t]);

    const showInvalidRouteCenter =
        !isProductionCentersLoading &&
        !!productionCenter &&
        resolvedRouteCenterNames.length === 0;
    const showNoProductionCenters =
        !isProductionCentersLoading && productionCenters.length === 0;
    const showNoSelectedCenters =
        !showInvalidRouteCenter &&
        !isProductionCentersLoading &&
        productionCenters.length > 0 &&
        selectedCenterNames.length === 0;

    return (
        <>
            <HelmetMeta title={t("kds.helmetTitle")} />
            <Box
                sx={{
                    p: 2,
                    height: "calc(100vh - 0px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    bgcolor: "background.default",
                }}
            >
                <KitchenDashboardHeader
                    t={t}
                    title={selectedCenterLabel}
                    pendingOrdersCount={pendingOrders.length}
                    productionCenters={productionCenters}
                    selectedCenterNames={selectedCenterNames}
                    onChangeCenterNames={setSelectedCenterNames}
                    onRefresh={() => refetch?.()}
                    isFetching={isFetching}
                />

                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {isProductionCentersLoading ? (
                        <Card
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "divider", p: 3, maxWidth: 720 }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                {t("kds.loadingProductionCenter")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t("common.pleaseWait")}
                            </Typography>
                        </Card>
                    ) : showInvalidRouteCenter ? (
                        <Card
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "divider", p: 3, maxWidth: 720 }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                No production center with the name {"{"}
                                {productionCenter || ""}
                                {"}"}
                            </Typography>
                        </Card>
                    ) : showNoProductionCenters ? (
                        <Card
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "divider", p: 3, maxWidth: 720 }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                No production centers found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Add at least one production center to display KDS orders.
                            </Typography>
                        </Card>
                    ) : showNoSelectedCenters ? (
                        <Card
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "divider", p: 3, maxWidth: 720 }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                {t("productionCenter.selectCenterHint")}
                            </Typography>
                        </Card>
                    ) : selectedOrder ? (
                        <KitchenOrderDetailsCard
                            t={t}
                            selectedOrder={selectedOrder}
                            selectedItems={getDisplayItems(selectedOrder)}
                            selectedLate={isOrderLate({
                                order: selectedOrder,
                                lastFoodReadyAt: lastFoodReadyAtByOrderName.get(selectedOrder?.name),
                                thresholdMinutes,
                                enabled: foodLateTimeEnabled,
                                nowMs: nowTs,
                            })}
                            distanceLocale={distanceLocale}
                            usersByName={usersByName}
                            showItemsOnKds={showItemsOnKds}
                            onAdvanceItem={handleAdvanceItem}
                            isItemDisabled={isItemActionDisabled}
                            getNextStatus={getNextStatus}
                            onClose={() => setDetailOrderName(null)}
                            onMarkReady={markOrderReady}
                            isMarkReadyLoading={
                                (markReadyMutation.isLoading &&
                                    activeOrderReadyName === selectedOrder?.name) ||
                                (pendingItemIdsByOrder[selectedOrder?.name] || []).length > 0
                            }
                        />
                    ) : isLoading ? (
                        <Typography variant="body2" color="text.secondary">
                            {t("kds.loadingOrders")}
                        </Typography>
                    ) : pendingOrders.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t("kds.noPendingOrders")}
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(1, minmax(0, 1fr))",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    md: "repeat(3, minmax(0, 1fr))",
                                    lg: "repeat(4, minmax(0, 1fr))",
                                    xl: "repeat(5, minmax(0, 1fr))",
                                },
                                gap: 1.5,
                                alignContent: "start",
                            }}
                        >
                            {pendingOrders.map((order) => (
                                <KitchenOrderCard
                                    key={order?.name}
                                    order={order}
                                    itemsForCenter={getDisplayItems(order)}
                                    flash={flashByOrderName?.[order?.name]}
                                    thresholdMinutes={thresholdMinutes}
                                    foodLateTimeEnabled={foodLateTimeEnabled}
                                    lastFoodReadyAt={lastFoodReadyAtByOrderName.get(order?.name)}
                                    nowTs={nowTs}
                                    distanceLocale={distanceLocale}
                                    t={t}
                                    showItemsOnKds={showItemsOnKds}
                                    onAdvanceItem={handleAdvanceItem}
                                    isItemDisabled={isItemActionDisabled}
                                    getNextStatus={getNextStatus}
                                    onMarkReady={markOrderReady}
                                    onShowDetails={setDetailOrderName}
                                    isMarkReadyLoading={
                                        (markReadyMutation.isLoading &&
                                            activeOrderReadyName === order?.name) ||
                                        (pendingItemIdsByOrder[order?.name] || []).length > 0
                                    }
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );
}

export default KitchenDashboard;
